import React from 'react';
import CombinedEditableSection from '../../../../../../components/CombinedEditableSection.comp';
import { parseInlineMarkdown } from '../../../../../utils/LearnTab/slideTextRenderer';
import {
  SlideList,
  HtmlContentColumn,
  HtmlContentWrapper,
  SlideTitle,
  PointText,
} from '../../../../../styles/LearnTab/slideBase.styles';
import { SlideTitleHeader, isSameAsTitle } from '../common/SlideTitleHeader.comp';
import { renderTextWithId } from '../common/EditableSlideField.comp';

export const ListSlide = ({
  slide,
  highlightWord,
  isEditMode = false,
  slideEdits = {},
  showEnglishContent = false,
  onSaveEdit,
  onResetEdit,
  onCancelEdit,
}) => {
  const content = slide?.SlideContent;
  const isString = typeof content === 'string';
  const hasListTags = isString && /<(ul|ol|li)[\s>]/i.test(content);

  const cleanItems = Array.isArray(content) ? content.filter(Boolean) : [content];
  const origin = slide?._pbdq_origin || slide || {};
  const isKeyPoints = slide?._pbdq_kind === 'key_points' || origin.slide_type === 'key_points';

  const textRendererOptions = { slideEdits, showEnglishContent, highlightWord };

  if (isKeyPoints) {
    const rawIntro = origin.intro_line || origin.section_title || '';
    const intro = isSameAsTitle(rawIntro, slide) ? '' : rawIntro;
    const closing = origin.closing_line || '';
    const points = origin.points || [];

    let parsedPoints = points;
    if ((!parsedPoints || parsedPoints.length === 0) && cleanItems && cleanItems.length > 0) {
      parsedPoints = cleanItems.map(item => {
        const str = String(item).trim();
        // Clean leading bullets if present
        const cleanStr = str.replace(/^[•\-*\s]+/, '');
        // Extract emoji if present at start
        const emojiMatch = cleanStr.match(/^([✀-➿]|[-]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|[‑-⛿]|\uD83E[\uDD10-\uDDFF])\s*(.*)$/);
        let icon = '';
        let rest = cleanStr;
        if (emojiMatch) {
          icon = emojiMatch[1];
          rest = emojiMatch[2];
        }
        // Extract verb_phrase and description split by ' — ' or ': '
        const parts = rest.split(/\s*[—\-:]\s*/);
        let verb_phrase = rest;
        let description = '';
        if (parts.length > 1) {
          verb_phrase = parts[0];
          description = parts.slice(1).join(' — ');
        }
        return { icon, verb_phrase, description };
      });
    }

    return (
      <HtmlContentColumn>
        <SlideTitleHeader
          slide={slide}
          slideEdits={slideEdits}
          showEnglishContent={showEnglishContent}
          highlightWord={highlightWord}
        />
        <HtmlContentWrapper>
          {intro && (
            <div className="intro-line">
              {parseInlineMarkdown(intro)}
            </div>
          )}
          {parsedPoints && parsedPoints.length > 0 && (
            <ul className="key-points">
              {parsedPoints.map((p, idx) => {
                const hasIcon = Boolean(typeof p.icon === 'string' ? p.icon.trim() : p.icon);
                return (
                  <li key={idx} className={hasIcon ? 'has-icon' : 'no-icon'}>
                    {hasIcon && <span className="icon">{p.icon}</span>}
                    <div className="key-point-copy">
                      <div className="key-point-phrase">
                        <strong>{parseInlineMarkdown(p.verb_phrase)}</strong>
                      </div>
                      {p.description && (
                        <div className="key-point-desc">
                          {parseInlineMarkdown(p.description)}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          {closing && (
            <div className="key-points-closing">
              {parseInlineMarkdown(closing)}
            </div>
          )}
        </HtmlContentWrapper>
      </HtmlContentColumn>
    );
  }

  return (
    <SlideList>
      {isEditMode ? (
        <CombinedEditableSection
          slide={slide}
          isEditMode={isEditMode}
          slideEdits={slideEdits}
          showEnglishContent={showEnglishContent}
          onSave={onSaveEdit}
          onReset={onResetEdit}
          onCancel={onCancelEdit}
          TitleComponent={SlideTitle}
          ContentComponent={PointText}
        />
      ) : (
        <>
          <SlideTitleHeader
            slide={slide}
            slideEdits={slideEdits}
            showEnglishContent={showEnglishContent}
            highlightWord={highlightWord}
          />
          {hasListTags || !Array.isArray(content) ? (
            renderTextWithId('content_0', content, PointText, {}, textRendererOptions)
          ) : (
            <ul>
              {content.map((item, idx) => {
                const cleanText = typeof item === 'string' ? item.replace(/^[•\-*\s]+/, '') : item;
                return (
                  <li key={idx}>
                    {renderTextWithId(`content_${idx}`, cleanText, PointText, {}, textRendererOptions)}
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
    </SlideList>
  );
};

export default ListSlide;
